import React, { useCallback, useMemo, useState } from "react";
import { Combobox, ComboboxOption } from "@panda0/panda0-ui";
import { ChevronDown, ChevronUp, CircleX } from "lucide-react";

import { SkillData, SubskillData } from "./types";

const SubskillField = ({
  skill,
  onSubskillChange,
}: {
  skill: SkillData;
  onSubskillChange: (id: string, subskills: SubskillData[]) => void;
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [tempSelection, setTempSelection] = useState(skill.subskills);

  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen((prev) => !prev);
    setTempSelection(skill.subskills);
  }, [skill.subskills]);

  const handleRemoveSubtag = useCallback(
    (subSkillToRemove: string) => {
      const updatedSubskills = tempSelection.filter(
        (subskill) => subskill.name !== subSkillToRemove
      );
      setTempSelection(updatedSubskills);
      onSubskillChange(skill.name, updatedSubskills);
    },
    [tempSelection, onSubskillChange, skill.name]
  );

  const selectedSkills = useMemo(
    () =>
      tempSelection.map((subskill) => ({
        value: subskill.weightage.toString(),
        label: subskill.name,
      })),
    [tempSelection]
  );

  const options = useMemo(
    () => [
      { value: "select-all", label: "(Select All)" },
      ...skill.subskills.map((subskill) => ({
        value: subskill.weightage.toString(),
        label: subskill.name,
      })),
    ],
    [skill.subskills]
  );

  const handleSelectionChange = useCallback(
    (selectedSubSkills: ComboboxOption<SubskillData>[]) => {
      if (
        selectedSubSkills.some((subskill) => subskill.label === "select-all")
      ) {
        setTempSelection(
          tempSelection.length === skill.subskills.length ? [] : skill.subskills
        );
      } else {
        setTempSelection(
          selectedSubSkills.map((x) => ({
            weightage: Number(x.value),
            name: x.label as string,
          }))
        );
      }
      onSubskillChange(
        skill.name,
        selectedSubSkills.map((x) => ({
          weightage: Number(x.value),
          name: x.label as string,
        }))
      );
    },
    [tempSelection, skill.subskills, skill.name, onSubskillChange]
  );

  return (
    <div className="flex flex-col rounded-xl p-1 w-full">
      <div className="flex items-center justify-between mb-1">
        <div className="flex flex-wrap gap-1 font-semibold">
          {selectedSkills.map((selectedSkill, index) => (
            <div
              key={index}
              className="flex items-center text-sm px-1.5 py-0.5 bg-neutral-100 rounded-md transition-transform hover:bg-neutral-200"
            >
              <span>{selectedSkill.label}</span>
              <button
                onClick={() => handleRemoveSubtag(selectedSkill.label)}
                className="ml-1 flex items-center"
                aria-label={`Remove ${selectedSkill.label}`}
              >
                <CircleX className="w-4 h-4" />
              </button>
            </div>
          ))}
          {selectedSkills.length === 0 && (
            <span className="text-neutral-400 text-sm">
              No subskills selected
            </span>
          )}
        </div>
        <button
          onClick={toggleDropdown}
          className="p-1 rounded-md hover:bg-neutral-100"
          aria-label="Toggle subtags"
        >
          {isDropdownOpen ? (
            <ChevronUp className="w-5 h-5 text-neutral-600" />
          ) : (
            <ChevronDown className="w-5 h-5 text-neutral-600" />
          )}
        </button>
      </div>
      {isDropdownOpen && (
        <div className="relative mt-1">
          <Combobox
            multiple
            caret={true}
            placeholder={
              <div className="h-4 w-3/4 animate-pulse rounded bg-neutral-200" />
            }
            open={isDropdownOpen}
            selected={selectedSkills}
            setSelected={handleSelectionChange}
            options={options}
            searchPlaceholder="Search or add subskills..."
          />
        </div>
      )}
    </div>
  );
};

export default React.memo(SubskillField);
